import * as React from "react";
import dayjs from "dayjs";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { Button, Empty, Spin, Tooltip } from "antd";
import { CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { notificationsStore } from "src/common/notificationsStore";
import { classNames } from "src/common/classNames";
import { onError } from "src/common/onError";
import { viewDateTimeUtc } from "src/common/formats";
import { NotificationText } from "./NotificationText";
import { OptionalLink } from "./OptionalLink";
import { NotifyAttachment } from "./NotifyAttachment";
import styles from "./ExtNotoficationsPopup.module.less";
/* eslint jsx-a11y/click-events-have-key-events: "off" */
/* eslint jsx-a11y/control-has-associated-label: "off" */
/* eslint jsx-a11y/no-static-element-interactions: "off" */

interface PropsExtNotoficationsPopup {
  open: boolean;
  close(): void;
}

export const ExtNotoficationsPopup: React.FC<PropsExtNotoficationsPopup> =
  observer((props) => {
    const { open, close } = props;
    const { unreaded, notificationsLoading, toReaded, isAllReaded } =
      notificationsStore;
    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Escape") close();
    };
    React.useEffect(() => {
      if (!open) return undefined;
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("keydown", onKey);
        notificationsStore.saveReaded().catch(onError);
      };
    }, [open]);

    // При клике на ссылку логично закрыть попап.
    // Возможно, было бы логично отметить сообщение как прочитанное. Но таких требований не было
    // Если всё-таки захотят, надо добавить вызов notificationsStore.addReaded
    const onLink = () => {
      close();
    };

    if (!open) return null;
    return (
      <>
        <div className={styles.mask} onClick={() => close()} />
        <div className={styles.panel}>
          <div className={styles.header}>
            <div className={styles.title}>{t("Notifications")}</div>
            {!isAllReaded && (
              <Button
                size="small"
                type="text"
                onClick={() => notificationsStore.addReadedAll()}
              >
                <div className={styles.allReaded}>
                  <div className={styles.readButton} />
                  <div>{t("Mark everything as read")}</div>
                </div>
              </Button>
            )}
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={() => notificationsStore.updateNotifications()}
              title={t("Refresh")}
              disabled={notificationsLoading}
            />
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={close}
              title={t("Close")}
            />
          </div>
          <div className={styles.listBox}>
            <Spin spinning={notificationsLoading}>
              {unreaded.length === 0 && <Empty />}
              {unreaded.map((it) => (
                <div key={it.id} className={styles.listItem}>
                  <div className={styles.itemLeftPart}>
                    <div className={styles.time}>
                      {timeFormat(it.eventTimestamp)}
                    </div>
                    <div className={styles.itemTitle}>{it.subject}</div>
                    {it.entityName && (
                      <div className={styles.entityName}>{it.entityName}</div>
                    )}
                    <NotificationText
                      text={it.text}
                      after={
                        <OptionalLink info={it} onClick={onLink} owner="text" />
                      }
                    />
                    <NotifyAttachment info={it} onLink={onLink} />
                  </div>
                  <div className={styles.itemRightPart}>
                    <Readed
                      onClick={() => notificationsStore.addReaded(it)}
                      readed={toReaded.has(it.id)}
                    />
                  </div>
                </div>
              ))}
            </Spin>
          </div>
        </div>
      </>
    );
  });

const timeFormat = (srcTime: string): string => {
  const time = dayjs(srcTime);
  if (!time.isValid) return srcTime;
  // TODO: Взято с макета. Однако это может быть неправильно для других локалей
  return viewDateTimeUtc(srcTime, "DD.MM.YYYY • HH:mm");
};

interface PropsReaded {
  readed: boolean;
  onClick: () => void;
}

const Readed: React.FC<PropsReaded> = ({ onClick, readed }) => {
  if (readed) return null;
  return (
    <Tooltip
      title={t("Marked by reading")}
      placement="bottomRight"
      mouseEnterDelay={1}
    >
      <button
        type="button"
        className={classNames([
          styles.readButton,
          [readed, styles.invisibleButton],
        ])}
        onClick={onClick}
      />
    </Tooltip>
  );
};
